import { parseCricketMatchName, parseFootballTennisMatchData } from "../../Games/matchUtils";
import {
    formatHandicap,
    isCricketMarketDisplayOne,
    isDecimalMarketSettledStatusFinal,
} from "../../Trader/Cricket/decimalMarketUtils";
import type {
    BullionLobbyUserActiveGame,
    BullionMarket,
    BullionPlayState,
    BullionPublicGame,
    BullionRoom,
    BullionRunner,
} from "./types";
import { BULLION_GROUP_COUNT, BULLION_LOBBY_MAX_MATCHES } from "./constants";
import {
    compareBullionCricketRawMarketsForOrdering,
    getBullionCricketMarketGroupIndex,
    isBullionTraderCricketMarket,
    BULLION_CRICKET_BOARD_TYPE_IDS,
    bullionMarketStableId,
    clampBullionGroupIndex,
} from "./bullionCricketMarketGroups";
import { isBullionInplayOnlyMarketTypeId } from "./bullionInplayTypeIds";
import { bullionOddsSnapshotMarkets } from "./bullionOddsSnapshot";
import type { RawMarket, RawMatch, RawRunner } from "./bullionRawMatch";
import { bullionLobbyFixturePhase, isRawMatchLiveForBullionLobby } from "./bullionRawMatch";
import { canonicalBullionDecimalMarketLineId } from "./bullionDecimalIds";
import { bullionBoardRawMarketsForMatch } from "./bullionFrozenBoardMarkets";
import { resolveBullionFixtureScheduledStartRaw } from "./bullionFixtureScheduledStart";
import { bullionCricketFormatOversFromMatch } from "../../Cricket/cricketMatchCardOverDisplay";

function toOdds(raw: RawRunner): number | null {
    const direct = raw.odds != null ? Number(raw.odds) : Number.NaN;
    if (Number.isFinite(direct) && direct > 1) return direct;
    const fallback = raw.recommended_price != null ? Number(raw.recommended_price) : Number.NaN;
    if (Number.isFinite(fallback) && fallback > 1) return fallback;
    return null;
}

function isTossWinnerMarket(market: RawMarket): boolean {
    const id = market.market_type_id != null ? String(market.market_type_id) : "";
    const name = String(market.market_name || market.market_type || market.name || "").toLowerCase();
    return id === "815" || name.includes("toss winner");
}

function selectionDisplayName(runner: RawRunner, index: number): string {
    const raw = runner.name;
    if (typeof raw === "string") return raw.trim();
    if (raw != null && typeof raw === "object") {
        const o = raw as Record<string, unknown>;
        if (typeof o.team === "string" && o.team.trim()) return o.team.trim();
        if (typeof o.player === "string" && o.player.trim()) return o.player.trim();
        if (typeof o.text === "string" && o.text.trim()) return o.text.trim();
        try {
            return JSON.stringify(raw).slice(0, 120);
        } catch {
            return "";
        }
    }
    return (
        (runner["selection-name"] || "").trim() ||
        (runner["participant-name"] || "").trim() ||
        (runner.id != null ? `Selection ${runner.id}` : "").trim()
    );
}

/** Trailing numeric line sometimes embedded in market name (Trader uses this when `line` is absent). */
function lineFromMarketNameEnd(name: string): number | null {
    const m = String(name || "")
        .trim()
        .match(/(\d+\.?\d*)\s*$/);
    if (!m) return null;
    const n = parseFloat(m[1]);
    return Number.isFinite(n) && n !== 0 ? n : null;
}

function parsePositiveLine(value: unknown): number | null {
    if (value == null || value === "") return null;
    const n = parseFloat(String(value));
    return Number.isFinite(n) && n !== 0 ? n : null;
}

/** Best-effort total/handicap for Over/Under from market + selection payload. */
function pickNumericLineFromRaw(market: RawMarket, runnersRaw: RawRunner[]): number | null {
    const m = market as Record<string, unknown>;
    for (const key of ["line", "Line", "handicap", "hcp", "total", "market-line", "points"] as const) {
        const hit = parsePositiveLine(m[key]);
        if (hit != null) return hit;
    }
    for (const run of runnersRaw) {
        const o = run as Record<string, unknown>;
        for (const key of ["line", "Line", "handicap", "hcp", "points", "selection-line"] as const) {
            const hit = parsePositiveLine(o[key]);
            if (hit != null) return hit;
        }
    }
    for (const run of runnersRaw) {
        const rawName = typeof run.name === "string" ? run.name.trim() : "";
        const embedded = rawName.match(/\b(?:over|under)\s+(\d+\.?\d*)\b/i);
        if (embedded) {
            const n = parseFloat(embedded[1]);
            if (Number.isFinite(n) && n !== 0) return n;
        }
    }
    return lineFromMarketNameEnd(String(market.name || market.market_name || ""));
}

function isOverUnderRunnerNames(names: string[]): boolean {
    if (names.length !== 2) return false;
    const kinds = names.map((n) => {
        const t = n.trim().toLowerCase();
        if (t === "over" || /^over(\s|$)/i.test(n)) return "O";
        if (t === "under" || /^under(\s|$)/i.test(n)) return "U";
        return "";
    });
    return kinds.includes("O") && kinds.includes("U");
}

function normalizeBullionMarketTypeId(raw: unknown): string {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    if (/^\d+(\.0+)?$/.test(s)) return String(parseInt(s, 10));
    return s;
}

/** Session-runs overs Bullion keeps: T20 → First 6; 50-over / ODI → First 10. */
function bullionPreferredSessionRunsOvers(formatOvers: number | null | undefined): number {
    return formatOvers === 50 ? 10 : 6;
}

/**
 * BULLION: only offer the format's preferred "Runs in First N …" session lines
 * (T20: 6, ODI: 10), not other First 10/12/15 siblings (same family as Trader session tabs).
 */
function shouldExcludeBullionRunsInFirstNonPreferredSession(
    market: RawMarket,
    formatOvers: number | null | undefined
): boolean {
    const blob = `${market.market_type || ""} ${market.name || ""} ${market.market_name || ""}`;
    if (!/runs in first/i.test(blob)) return false;
    const m = blob.match(/runs in first\s*(\d+)/i);
    if (!m) return false;
    const overs = parseInt(m[1], 10);
    return Number.isFinite(overs) && overs !== bullionPreferredSessionRunsOvers(formatOvers);
}

function bullionCricketShouldPutLineOnSelections(market: RawMarket, normalizedRunnerNames: string[]): boolean {
    if (!isOverUnderRunnerNames(normalizedRunnerNames)) return false;
    const tid = normalizeBullionMarketTypeId(market.market_type_id);
    if (tid && BULLION_CRICKET_BOARD_TYPE_IDS.has(tid)) return true;
    const t = String(market.market_type || market.market_name || market.name || "").toLowerCase();
    if (t.includes("innings fours") || t.includes("innings sixes")) return true;
    if (t.includes("match runs") || t.includes("runs in match")) return true;
    if (t.includes("match fours") || t.includes("match sixes")) return true;
    if (t.includes("match wickets") || t.includes("match run outs")) return true;
    if (t.includes("match highest")) return true;
    if (t.includes("session runs") || t.includes("runs in session")) return true;
    if (t.includes("fall of first wicket")) return true;
    return false;
}

/** Remove a trailing total/handicap when it duplicates the structured line (feed often puts line only in `line`, not title). */
function stripTrailingTotalLineFromTitle(title: string, lineNum: number | null): string {
    const trimmed = String(title || "").trim();
    if (lineNum == null || !Number.isFinite(lineNum)) return trimmed;
    const fmt = formatHandicap(lineNum);
    if (fmt) {
        const low = trimmed.toLowerCase();
        const suffix = ` ${fmt}`.toLowerCase();
        if (low.endsWith(suffix)) return trimmed.slice(0, trimmed.length - suffix.length).trim();
    }
    const m = trimmed.match(/^(.*?)\s+(\d+\.?\d*)\s*$/);
    if (m) {
        const tail = parseFloat(m[2]);
        if (Number.isFinite(tail) && Math.abs(tail - lineNum) < 1e-6) return m[1].trim();
    }
    return trimmed;
}

function labelBullionOverUnderRunnersWithLine(runners: BullionRunner[], lineNum: number): BullionRunner[] {
    const fmt = formatHandicap(lineNum);
    if (!fmt) return runners;
    return runners.map((r) => {
        const raw = String(r.name || "").trim();
        const low = raw.toLowerCase();
        if (low === "over") return { ...r, name: `Over ${fmt}` };
        if (low === "under") return { ...r, name: `Under ${fmt}` };
        return r;
    });
}

/** Append visible line to Over/Under markets when the feed does not include it in `name`. */
function bullionMarketDisplayTitle(market: RawMarket, runnersRaw: RawRunner[], normalizedRunnerNames: string[]): string {
    const base = String(market.name || market.market_name || market.market_type || "Market").trim();
    if (!isOverUnderRunnerNames(normalizedRunnerNames)) return base;
    const lineNum = pickNumericLineFromRaw(market, runnersRaw);
    if (lineNum == null) return base;
    const formatted = formatHandicap(lineNum);
    if (!formatted) return base;
    if (base.includes(formatted)) return base;
    return `${base} ${formatted}`;
}

function runnerImageUrlFromRaw(runner: RawRunner): string | undefined {
    const raw = runner.image ?? runner.Image ?? runner.photo ?? runner.image_url ?? runner.imageUrl;
    if (raw == null) return undefined;
    const s = String(raw).trim();
    return s || undefined;
}

function normalizeRunner(runner: RawRunner, index: number, forcedOdds?: number): BullionRunner | null {
    const name = selectionDisplayName(runner, index);
    if (!name) return null;
    const id = String(runner.id ?? `${name}-${index}`);
    const image = runnerImageUrlFromRaw(runner);
    const base: BullionRunner = {
        id,
        name,
        odds: forcedOdds != null ? forcedOdds : toOdds(runner),
        status: runner.status != null ? String(runner.status) : undefined,
        winner:
            runner.winner === true ||
            runner.winner === 1 ||
            runner.winner === "1",
    };
    if (image) base.image = image;
    return base;
}

function bullionRunnerIsOverSide(name: string): boolean {
    const raw = String(name || "").trim();
    if (!raw) return false;
    return /^over\b/i.test(raw);
}

function bullionRunnerIsUnderSide(name: string): boolean {
    const raw = String(name || "").trim();
    if (!raw) return false;
    return /^under\b/i.test(raw);
}

function bullionOverUnderBalance(runners: BullionRunner[]): number | null {
    const over = runners.find((r) => bullionRunnerIsOverSide(String(r.name || "")));
    const under = runners.find((r) => bullionRunnerIsUnderSide(String(r.name || "")));
    const overOdds = over?.odds;
    const underOdds = under?.odds;
    if (
        overOdds == null ||
        underOdds == null ||
        !Number.isFinite(Number(overOdds)) ||
        !Number.isFinite(Number(underOdds))
    ) {
        return null;
    }
    return Math.abs(Number(overOdds) - Number(underOdds));
}

function bullionLineVariantBaseName(name: string): string {
    return String(name || "")
        .toLowerCase()
        .replace(/\s+\d+(\.\d+)?\s*$/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

/** Title + numeric line + O/U balance for cricket duplicate-line collapse (display title may strip the handicap). */
function bullionCricketLineDedupeBasis(row: { raw: RawMarket; market: BullionMarket }): {
    runnersRawForRow: RawRunner[];
    numericLine: number | null;
    balance: number | null;
    variantKey: string;
} {
    const runnersRawForRow = Array.isArray(row.raw.selectionslist)
        ? row.raw.selectionslist
        : Array.isArray(row.raw.selections)
          ? row.raw.selections
          : Array.isArray(row.raw.runners)
            ? row.raw.runners
            : [];
    const titleForVariant = String(row.raw.name ?? row.raw.market_name ?? row.market.name ?? "").trim();
    const numericLine =
        lineFromMarketNameEnd(titleForVariant) ??
        lineFromMarketNameEnd(row.market.name) ??
        pickNumericLineFromRaw(row.raw, runnersRawForRow);
    const balance = bullionOverUnderBalance(row.market.runners);
    const typeId = normalizeBullionMarketTypeId(row.raw.market_type_id);
    const propSuffix = row.market.groupIndex === 1 && typeId ? `|${typeId}` : "";
    const variantKey = `${row.market.groupIndex}${propSuffix}|${bullionLineVariantBaseName(titleForVariant)}`;
    return { runnersRawForRow, numericLine, balance, variantKey };
}

function bullionRawMarketResultSettledForPlayedView(raw: RawMarket): boolean {
    const r = raw as Record<string, unknown>;
    const ss = r.settled_status ?? r.Settled_status;
    return isDecimalMarketSettledStatusFinal(ss);
}

function normalizeMarkets(markets: RawMarket[] | undefined, match: RawMatch): BullionMarket[] {
    if (!Array.isArray(markets)) return [];
    const sportUpper = String(match.sport || "CRICKET").toUpperCase();
    const isCricket = sportUpper === "CRICKET";
    const format = match.format;
    const formatOvers = isCricket
        ? bullionCricketFormatOversFromMatch(match as Record<string, unknown>)
        : null;
    const normalized: BullionMarket[] = [];
    const cricketRows: { raw: RawMarket; market: BullionMarket }[] = [];
    const seenCricketKeys = new Set<string>();

    markets.forEach((market, marketIndex) => {
        const hasTypeMeta =
            market?.market_type_id != null ||
            (market?.market_type != null && String(market.market_type).trim() !== "");
        if (isCricket && hasTypeMeta && !isBullionTraderCricketMarket(market, format)) return;
        if (isCricket && isBullionInplayOnlyMarketTypeId(market.market_type_id)) return;
        // Frozen `bullion_games.odds_snapshot` slim rows omit `display` (null) — still board-eligible after filtering above.
        if (
            isCricket &&
            market.display != null &&
            String(market.display).trim() !== "" &&
            !isCricketMarketDisplayOne(market)
        ) {
            return;
        }
        if (isCricket && shouldExcludeBullionRunsInFirstNonPreferredSession(market, formatOvers)) return;

        const runnersRaw = Array.isArray(market.selectionslist)
            ? market.selectionslist
            : Array.isArray(market.selections)
              ? market.selections
              : Array.isArray(market.runners)
                ? market.runners
                : [];
        const forcedOdds = isTossWinnerMarket(market) ? 2.0 : undefined;
        const runners = runnersRaw
            .map((runner, runnerIndex) => normalizeRunner(runner, runnerIndex, forcedOdds))
            .filter((runner): runner is BullionRunner => runner != null);
        if (runners.length < 2) return;

        const runnerNames = runners.map((r) => r.name);
        const lineNumForDisplay = pickNumericLineFromRaw(market, runnersRaw);
        const lineOnSelections =
            isCricket &&
            lineNumForDisplay != null &&
            bullionCricketShouldPutLineOnSelections(market, runnerNames);
        const finalRunners =
            lineOnSelections && lineNumForDisplay != null
                ? labelBullionOverUnderRunnersWithLine(runners, lineNumForDisplay)
                : runners;

        const stableId = bullionMarketStableId(market, marketIndex);
        if (isCricket) {
            if (seenCricketKeys.has(stableId)) return;
            seenCricketKeys.add(stableId);
        }

        const groupIndex = isCricket
            ? clampBullionGroupIndex(getBullionCricketMarketGroupIndex(market))
            : marketIndex % BULLION_GROUP_COUNT;

        const displayName =
            lineOnSelections && lineNumForDisplay != null
                ? stripTrailingTotalLineFromTitle(
                      String(market.name || market.market_name || market.market_type || "Market").trim(),
                      lineNumForDisplay
                  )
                : bullionMarketDisplayTitle(market, runnersRaw, runnerNames);

        const decimalMarketId =
            market.market_id != null && String(market.market_id).trim() !== ""
                ? String(market.market_id)
                : null;
        const row: BullionMarket = {
            id: stableId,
            decimalMarketId,
            decimalMarketLineId: canonicalBullionDecimalMarketLineId(
                decimalMarketId,
                market.market_line_id != null && String(market.market_line_id).trim() !== ""
                    ? String(market.market_line_id)
                    : null
            ),
            marketTypeId:
                market.market_type_id != null && String(market.market_type_id).trim() !== ""
                    ? String(market.market_type_id)
                    : null,
            marketType:
                market.market_type != null && String(market.market_type).trim() !== ""
                    ? String(market.market_type)
                    : null,
            name: displayName,
            groupIndex,
            runners: finalRunners,
            settled: bullionRawMarketResultSettledForPlayedView(market),
        };

        if (isCricket) {
            cricketRows.push({ raw: market, market: row });
        } else {
            normalized.push(row);
        }
    });

    if (isCricket) {
        const teams = parseCricketMatchName(String(match.name || "")).teams;
        const teamA = teams[0] ?? "";
        const teamB = teams[1] ?? "";
        cricketRows.sort((x, y) => compareBullionCricketRawMarketsForOrdering(x.raw, y.raw, teamA, teamB));
        const bestByVariant = new Map<string, { row: { raw: RawMarket; market: BullionMarket }; balance: number }>();

        for (const row of cricketRows) {
            const { numericLine, balance, variantKey } = bullionCricketLineDedupeBasis(row);
            if (numericLine == null || balance == null) continue;
            const prev = bestByVariant.get(variantKey);
            if (!prev || balance < prev.balance) {
                bestByVariant.set(variantKey, { row, balance });
            }
        }

        return cricketRows
            .filter((row) => {
                const { numericLine, balance, variantKey } = bullionCricketLineDedupeBasis(row);
                if (numericLine == null || balance == null) return true;
                return bestByVariant.get(variantKey)?.row.market.id === row.market.id;
            })
            .map((r) => r.market);
    }
    return normalized;
}

function splitIntoGroups(markets: BullionMarket[]): BullionMarket[][] {
    const groups: BullionMarket[][] = Array.from({ length: BULLION_GROUP_COUNT }, () => []);
    markets.forEach((market) => {
        const groupIndex = clampBullionGroupIndex(market.groupIndex);
        groups[groupIndex].push({ ...market, groupIndex });
    });
    return groups;
}

/** Listed matches act as public Bullion rooms; include all fetched fixtures. */
function isListableBullionMatch(match: RawMatch): boolean {
    void match;
    return true;
}

function bullionMatchStartMs(match: RawMatch): number | null {
    const m = match as Record<string, unknown>;
    const raw = m.actual_start ?? match.start ?? m["starts-at"] ?? m.startTime ?? m.start_time;
    if (raw == null || raw === "") return null;
    const t = new Date(String(raw)).getTime();
    return Number.isFinite(t) ? t : null;
}

/** Scheduled kickoff for lobby cards, toss window, and entry lock (not `actual_start`). */
function bullionMatchStartRaw(match: RawMatch): string | undefined {
    return resolveBullionFixtureScheduledStartRaw(match) ?? undefined;
}

/** Non-cricket fallback when the feed has no usable markets (four groups, two-way picks). */
function buildFallbackRawMarkets(match: RawMatch): RawMarket[] {
    let sideA = "Side 1";
    let sideB = "Side 2";
    const parts = String(match.name || "")
        .split(" v ")
        .map((p) => p.trim())
        .filter(Boolean);
    if (parts.length >= 2) {
        sideA = parts[0];
        sideB = parts[1];
    }
    const mid = String(match.id ?? "x");
    const fallbackMeta: { market_type_id: string; market_type: string; name: string }[] = [
        { market_type_id: "1010", market_type: "Match Winner", name: "Match Winner" },
        { market_type_id: "410", market_type: "Runs in Over", name: `Runs in Over 1` },
        { market_type_id: "210", market_type: "Innings Runs", name: "Innings Runs" },
        { market_type_id: "910", market_type: "Batter Runs", name: "Batter Runs" },
    ];
    return [0, 1, 2, 3].map((i) => ({
        id: `bullion-fallback-${mid}-${i}`,
        market_type_id: fallbackMeta[i].market_type_id,
        market_type: fallbackMeta[i].market_type,
        name: fallbackMeta[i].name,
        runners: [
            { id: `${mid}-a-${i}`, name: sideA, odds: 1.91 },
            { id: `${mid}-b-${i}`, name: sideB, odds: 1.91 },
        ],
    }));
}

function extractCompetition(match: RawMatch): string {
    if ((match.competition || "").trim()) return String(match.competition).trim();
    const sport = String(match.sport || "").toLowerCase();
    if (sport === "cricket") {
        return parseCricketMatchName(match.name || "").competition || "Cricket";
    }
    return parseFootballTennisMatchData(match as any).competition || sport.toUpperCase();
}

export function buildBullionRooms(matches: RawMatch[], maxRooms: number = BULLION_LOBBY_MAX_MATCHES): BullionRoom[] {
    return matches
        .filter(isListableBullionMatch)
        .slice(0, maxRooms)
        .map((match, index) => {
            const sportUpper = String(match.sport || "CRICKET").toUpperCase();
            const rawCombined = bullionBoardRawMarketsForMatch(match);
            let markets = normalizeMarkets(rawCombined, match);
            if (markets.length === 0 && sportUpper !== "CRICKET") {
                markets = normalizeMarkets(buildFallbackRawMarkets(match), match);
            }
            return {
                id: `bullion-${String(match.id ?? index)}`,
                name: `BULLION room ${index + 1}`,
                matchName: String(match.name || "").trim(),
                matchId: String(match.id ?? index),
                sport: String(match.sport || "CRICKET").toUpperCase(),
                competition: extractCompetition(match),
                startsAt: bullionMatchStartRaw(match),
                status: match.status ? String(match.status) : undefined,
                isLive: isRawMatchLiveForBullionLobby(match),
                fixturePhase: bullionLobbyFixturePhase(match),
                groups: splitIntoGroups(markets),
                players: Math.max(2, Math.floor(Math.random() * 16) + 2),
            };
        })
        .filter((room) => {
            if (String(room.sport || "").toUpperCase() === "CRICKET") return true;
            return room.groups.some((group) => group.length > 0);
        });
}

export type BullionPlayBootstrapLike = {
    matchId: string;
    matchName?: string | null;
    competition?: string | null;
    startsAt?: string | null;
    oddsSnapshot?: unknown | null;
};

export function buildBullionRoomFromPlayBootstrap(b: BullionPlayBootstrapLike): BullionRoom {
    const matchId = String(b.matchId || "").trim();
    const matchName = String(b.matchName || "").trim() || "Match " + matchId;
    const snapRows = bullionOddsSnapshotMarkets(b.oddsSnapshot);
    const rawSeed = {
        id: matchId,
        name: matchName,
        sport: "cricket",
        competition:
            b.competition != null && String(b.competition).trim() !== "" ? String(b.competition).trim() : undefined,
        markets: snapRows,
    };
    if (b.startsAt) {
        (rawSeed as any).start = b.startsAt;
        (rawSeed as any)["starts-at"] = b.startsAt;
    }
    const built = buildBullionRooms([rawSeed as any], 1);
    const room = built[0];
    if (!room) {
        return {
            id: "bullion-" + matchId,
            name: "BULLION",
            matchName,
            matchId,
            sport: "CRICKET",
            competition: String(b.competition || "").trim() || "Cricket",
            startsAt: b.startsAt || undefined,
            isLive: false,
            skipBullionEntryCoinCheck: true,
            groups: [[], [], [], []],
            players: 2,
        };
    }
    return {
        ...room,
        isLive: false,
        skipBullionEntryCoinCheck: true,
    };
}

/** Prefer `next` but never drop non-empty `oddsSnapshot` from `prev` (stale list fallbacks / races). */
export function bullionPublicGameMergePreferExistingOdds(
    prev: BullionPublicGame | null | undefined,
    next: BullionPublicGame | null
): BullionPublicGame | null {
    if (!next) return prev ?? null;
    if (!prev) return next;
    if (Number(prev.gameId) !== Number(next.gameId)) return next;
    const prevN = bullionOddsSnapshotMarkets(prev.oddsSnapshot).length;
    const nextN = bullionOddsSnapshotMarkets(next.oddsSnapshot).length;
    if (nextN === 0 && prevN > 0) {
        return { ...next, oddsSnapshot: prev.oddsSnapshot };
    }
    return next;
}

/** Map `/bullion/open-game` `game` row into board state (odds snapshot for market hydration). */
export function bullionPublicGameFromOpenGameApiRow(
    row: Record<string, unknown> | null | undefined,
    matchId: string,
    gameId: number
): BullionPublicGame | null {
    if (!row || Number(row.gameId) !== gameId) return null;
    const playersCount = Number(row.playersCount ?? 0);
    const entryStake = Number(row.entryStake ?? 0);
    const prizePotRaw = row.prizePot;
    const prizePot =
        prizePotRaw != null && Number.isFinite(Number(prizePotRaw))
            ? Number(prizePotRaw)
            : playersCount * entryStake;
    const snap = row.oddsSnapshot ?? row.odds_snapshot ?? null;
    return {
        gameId,
        matchId: String(row.matchId ?? matchId),
        name: String(row.name ?? "").trim() || `Game ${gameId}`,
        entryStake,
        prizePot,
        oddsSnapshot: snap == null ? null : snap,
        visibility: String(row.visibility ?? "private"),
        status: String(row.status ?? "open"),
        playersCount,
        createdByUserId:
            row.createdByUserId != null
                ? Number(row.createdByUserId)
                : row.created_by_user_id != null
                  ? Number(row.created_by_user_id)
                  : null,
        creatorPlayerName:
            row.creatorPlayerName != null
                ? String(row.creatorPlayerName).trim() || null
                : row.creator_player_name != null
                  ? String(row.creator_player_name).trim() || null
                  : null,
        viewerIsCreator: Boolean(row.viewerIsCreator),
        inviteCode:
            row.inviteCode != null && String(row.inviteCode).trim() !== ""
                ? String(row.inviteCode).trim()
                : null,
        hostCanDeleteBattle:
            typeof row.hostCanDeleteBattle === "boolean" ? row.hostCanDeleteBattle : playersCount === 0,
    };
}

export function bullionLobbyUserActiveGameToPublicGame(matchId: string, g: BullionLobbyUserActiveGame): BullionPublicGame {
    return {
        gameId: g.gameId,
        matchId,
        name: g.name || "Game " + g.gameId,
        entryStake: g.entryStake,
        visibility: "public",
        status: g.gameStatus || "open",
        playersCount: 0,
    };
}

export function bullionPlayStateToPublicGame(play: BullionPlayState): BullionPublicGame {
    const entry = Number(play.entryStake ?? 0);
    return {
        gameId: play.gameId,
        matchId: String(play.matchId || "").trim(),
        name: String(play.gameDisplayName || "").trim() || "Game " + play.gameId,
        entryStake: entry > 0 ? entry : 0,
        prizePot: play.prizePot,
        oddsSnapshot: play.oddsSnapshot ?? null,
        visibility: "public",
        status: String(play.gameStatus || "").trim() || "closed",
        playersCount: Math.max(0, Number(play.playerCount ?? 0)),
    };
}

export type { RawRunner, RawMarket, RawMatch, BullionLobbyFixturePhase } from "./bullionRawMatch";
export {
    isRawMatchFinishedForBullionLobby,
    isRawMatchLiveForBullionLobby,
    bullionLobbyFixturePhase,
    bullionRoomResolvedFixturePhase,
} from "./bullionRawMatch";
export type { BullionLobbySelectOpts } from "./bullionLobbyOrdering";
export {
    isT20BullionLobbyMatch,
    isF50BullionLobbyMatch,
    mergeBullionLobbySourceLists,
    pickPreservedLobbyRowsForActivePlays,
    selectAndOrderBullionLobbyMatches,
} from "./bullionLobbyOrdering";
export { enrichMatchesWithCricketStreamInitialState } from "./bullionStreamEnrich";
export {
    bullionSelectionRegularChips,
    totalRegularChips,
    bullionSelectionStorageKey,
    normalizeBullionSelectionMapKeys,
    normalizeBullionBonusPicks,
    coerceBullionBonusPicks,
    regularChipsInGroup,
    parseBullionPlaySelectionsFromApi,
} from "./bullionSelectionKeys";
